// file: frontend/src/components/pages/admin/personas/AddPersonaModal.jsx
import React, { useState, useEffect } from "react";
import { Loader2, X } from "lucide-react";

const AddPersonaModal = ({ isOpen, onSave, onClose, persona, loading }) => {
    const [form, setForm] = useState({
        name: "",
        internal_description: "",
        system_prompt: "",
        contextual_rules: [],
        disclaimer_text: "",
        example_interactions: [],
        metadata: {
            tags: [],
            version: "1.0.0"
        }
    });

    const [formErrors, setFormErrors] = useState({});
    const [currentTag, setCurrentTag] = useState("");



    // Reset form function - defined before useEffect to avoid initialization error
    const resetForm = () => {
        setForm({
            name: "",
            internal_description: "",
            system_prompt: "",
            contextual_rules: [],
            disclaimer_text: "",
            example_interactions: [],
            metadata: {
                tags: [],
                version: "1.0.0"
            }
        });
        setCurrentTag("");
    };

    // Add a new tag to the list
    const addTag = (tagName) => {
        const trimmedTag = tagName.trim();
        if (trimmedTag && !form.metadata.tags.includes(trimmedTag)) {
            setForm(prev => ({
                ...prev,
                metadata: {
                    ...prev.metadata,
                    tags: [...prev.metadata.tags, trimmedTag]
                }
            }));
        }
        setCurrentTag("");
    };

    // Remove a tag from the list
    const removeTag = (tagToRemove) => {
        setForm(prev => ({
            ...prev,
            metadata: {
                ...prev.metadata,
                tags: prev.metadata.tags.filter(tag => tag !== tagToRemove)
            }
        }));
    };

    // Handle tag input key events
    const handleTagKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            if (currentTag.trim()) {
                addTag(currentTag);
            }
        }
    };

    useEffect(() => {
        if (persona) {
            setForm({
                name: persona.name || "",
                internal_description: persona.internal_description || "",
                system_prompt: persona.system_prompt || "",
                contextual_rules: persona.contextual_rules || [],
                disclaimer_text: persona.disclaimer_text || "",
                example_interactions: persona.example_interactions || [],
                metadata: {
                    tags: persona.metadata?.tags || [],
                    version: persona.metadata?.version || "1.0.0"
                }
            });
        } else {
            resetForm();
        }
        setFormErrors({});
        setCurrentTag("");
    }, [persona, isOpen]);

    if (!isOpen) return null;

    const validateForm = () => {
        const errors = {};

        if (!form.name.trim()) {
            errors.name = "Name is required";
        }

        if (!form.internal_description.trim()) {
            errors.internal_description = "Description is required";
        }

        if (!form.system_prompt.trim()) {
            errors.system_prompt = "System prompt is required";
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));

        // Clear error when user starts typing
        if (formErrors[name]) {
            setFormErrors(prev => ({
                ...prev,
                [name]: ""
            }));
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        onSave(form);
    };

    const handleClose = () => {
        resetForm();
        setFormErrors({});
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-bg-60 rounded-xl shadow-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto relative">
                <button
                    className="absolute top-2 right-2 text-fg-60 hover:text-fg-50 text-2xl"
                    onClick={handleClose}
                    disabled={loading}
                >
                    &times;
                </button>
                <h2 className="text-lg font-bold mb-6">
                    {persona ? "Edit Persona" : "Add New Persona"}
                </h2>

                <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4">
                    <div>
                        <label className="block text-xs font-medium text-fg-60 mb-1">
                            Name *
                        </label>
                        <input
                            className={`w-full py-2 px-3 rounded-md bg-bg-50 text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 text-sm ${formErrors.name ? 'border border-red-500' : ''
                                }`}
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                            placeholder="e.g., Creative Writer"
                            disabled={loading}
                        />
                        {formErrors.name && (
                            <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>
                        )}
                    </div>



                    <div>
                        <label className="block text-xs font-medium text-fg-60 mb-1">
                            Description *
                        </label>
                        <textarea
                            className={`w-full py-2 px-3 rounded-md bg-bg-50 text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 text-sm resize-none ${formErrors.internal_description ? 'border border-red-500' : ''
                                }`}
                            name="internal_description"
                            value={form.internal_description}
                            onChange={handleChange}
                            placeholder="Brief description of the persona's role and capabilities"
                            rows={3}
                            disabled={loading}
                        />
                        {formErrors.internal_description && (
                            <p className="text-red-500 text-xs mt-1">{formErrors.internal_description}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-fg-60 mb-1">
                            System Prompt *
                        </label>
                        <textarea
                            className={`w-full py-2 px-3 rounded-md bg-bg-50 text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 text-sm resize-none ${formErrors.system_prompt ? 'border border-red-500' : ''
                                }`}
                            name="system_prompt"
                            value={form.system_prompt}
                            onChange={handleChange}
                            placeholder="Detailed instructions for how the AI should behave as this persona"
                            rows={6}
                            disabled={loading}
                        />
                        {formErrors.system_prompt && (
                            <p className="text-red-500 text-xs mt-1">{formErrors.system_prompt}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-fg-60 mb-1">
                            Contextual Rules
                        </label>
                        <textarea
                            className="w-full py-2 px-3 rounded-md bg-bg-50 text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 text-sm resize-none"
                            name="contextual_rules"
                            value={Array.isArray(form.contextual_rules) ? form.contextual_rules.join('\n') : ''}
                            onChange={(e) => setForm(prev => ({
                                ...prev,
                                contextual_rules: e.target.value.split('\n').filter(rule => rule.trim())
                            }))}
                            placeholder="Enter contextual rules (one per line)"
                            rows={4}
                            disabled={loading}
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-fg-60 mb-1">
                            Disclaimer Text
                        </label>
                        <textarea
                            className="w-full py-2 px-3 rounded-md bg-bg-50 text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 text-sm resize-none"
                            name="disclaimer_text"
                            value={form.disclaimer_text}
                            onChange={handleChange}
                            placeholder="Important disclaimers or warnings for users"
                            rows={3}
                            disabled={loading}
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-fg-60 mb-1">
                            Example Interactions
                        </label>
                        
                        {/* Display existing interactions */}
                        {Array.isArray(form.example_interactions) && form.example_interactions.length > 0 && (
                            <div className="space-y-3 mb-3">
                                {form.example_interactions.map((interaction, index) => (
                                    <div key={index} className="bg-bg-40 rounded-md p-3 border border-bd-50">
                                        <div className="grid grid-cols-1 gap-2">
                                            <div>
                                                <label className="block text-xs font-medium text-fg-60 mb-1">User:</label>
                                                <input
                                                    type="text"
                                                    className="w-full py-1.5 px-2 rounded bg-bg-50 text-fg-50 focus:outline-none focus:ring-1 focus:ring-ac-02 text-sm"
                                                    value={typeof interaction === 'object' ? interaction.user || '' : interaction}
                                                    onChange={(e) => {
                                                        const newInteractions = [...form.example_interactions];
                                                        if (typeof newInteractions[index] === 'object') {
                                                            newInteractions[index] = { ...newInteractions[index], user: e.target.value };
                                                        } else {
                                                            newInteractions[index] = { user: e.target.value, persona: '' };
                                                        }
                                                        setForm(prev => ({ ...prev, example_interactions: newInteractions }));
                                                    }}
                                                    disabled={loading}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-fg-60 mb-1">Persona:</label>
                                                <input
                                                    type="text"
                                                    className="w-full py-1.5 px-2 rounded bg-bg-50 text-fg-50 focus:outline-none focus:ring-1 focus:ring-ac-02 text-sm"
                                                    value={typeof interaction === 'object' ? (interaction.persona || interaction.assistant || '') : ''}
                                                    onChange={(e) => {
                                                        const newInteractions = [...form.example_interactions];
                                                        if (typeof newInteractions[index] === 'object') {
                                                            newInteractions[index] = { ...newInteractions[index], persona: e.target.value };
                                                        } else {
                                                            newInteractions[index] = { user: newInteractions[index], persona: e.target.value };
                                                        }
                                                        setForm(prev => ({ ...prev, example_interactions: newInteractions }));
                                                    }}
                                                    disabled={loading}
                                                />
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const newInteractions = form.example_interactions.filter((_, i) => i !== index);
                                                setForm(prev => ({ ...prev, example_interactions: newInteractions }));
                                            }}
                                            className="mt-2 text-xs text-fg-60 hover:text-red-400 transition-colors"
                                            disabled={loading}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                        
                        {/* Add new interaction button */}
                        <button
                            type="button"
                            onClick={() => {
                                const newInteractions = [...(form.example_interactions || []), { user: '', persona: '' }];
                                setForm(prev => ({ ...prev, example_interactions: newInteractions }));
                            }}
                            className="w-full py-2 px-3 rounded-md bg-bg-50 text-fg-60 hover:bg-bg-40 hover:text-fg-50 transition-colors text-sm"
                            disabled={loading}
                        >
                            + Add example interaction
                        </button>
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-fg-60 mb-1">
                            Tags
                        </label>
                        
                        {/* Display existing tags as chips */}
                        {form.metadata.tags.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2">
                                {form.metadata.tags.map((tag, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center gap-1 px-2 py-1 bg-ac-02 text-white dark:text-black text-xs rounded-full"
                                    >
                                        <span>{tag}</span>
                                        <button
                                            type="button"
                                            onClick={() => removeTag(tag)}
                                            className="hover:bg-red-600 rounded-full p-0.5 transition-colors"
                                            disabled={loading}
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                        
                        {/* Input for adding new tags */}
                        <input
                            className="w-full py-2 px-3 rounded-md bg-bg-50 text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 text-sm"
                            value={currentTag}
                            onChange={(e) => setCurrentTag(e.target.value)}
                            onKeyDown={handleTagKeyDown}
                            placeholder="Type a tag name and press Enter to add"
                            disabled={loading}
                        />
                    </div>

                    <div className="flex justify-end gap-2 mt-4">
                        <button
                            type="button"
                            className="px-3 py-1.5 text-xs rounded bg-bg-50 text-fg-60 font-semibold hover:bg-bg-40 disabled:opacity-50"
                            onClick={handleClose}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-3 py-1.5 text-xs rounded bg-ac-02 text-white dark:text-black  font-semibold hover:bg-ac-01 disabled:opacity-50 flex items-center gap-2"
                            disabled={loading}
                        >
                            {loading && <Loader2 className="animate-spin w-4 h-4" />}
                            {persona ? "Update Persona" : "Add Persona"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddPersonaModal;
